import React from "react";

const tiers = [
  {
    name: "Advisory",
    tagline: "Spatial Positioning Blueprint",
    price: "$2,500 – $5,000",
    cadence: "flat fee · per property",
    features: [
      "Initial discovery & objective mapping",
      "Yield & micro-market modelling",
      "Spatial redesign blueprint",
      "Compliance & tenancy audit",
      "Written, actionable engagement report",
    ],
    cta: "Begin with advisory",
    accent: false,
  },
  {
    name: "Project Oversight",
    tagline: "Refurb & Vendor Management",
    price: "12%",
    cadence: "of refurbishment contract value",
    features: [
      "Contractor sourcing & vetting",
      "Milestone & quality audits",
      "Material & invoice verification",
      "Independent dispute mediation",
      "Final hand-back inspection",
    ],
    cta: "Engage on a project",
    accent: true,
  },
  {
    name: "Portfolio Retainer",
    tagline: "PropTech & Ongoing Oversight",
    price: "$1,500",
    cadence: "per month · per unit",
    features: [
      "PMS, smart-lock & contactless setup",
      "Cleaning & maintenance supply chain",
      "Monthly remote performance audit",
      "Quarterly portfolio review",
      "Priority response on incidents",
    ],
    cta: "Hold the portfolio",
    accent: false,
  },
];

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 bg-[#EFEBE4] relative">
      <div className="grain" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="text-xs uppercase tracking-[0.28em] text-[#6E5A4E]">Engagement Models</span>
            <h2 className="font-serif-display mt-6 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#2B211A] leading-[1.05]">
              Transparent, performance-aligned <span className="italic text-[#8C735D]">fees</span>.
            </h2>
          </div>
          <p className="md:max-w-sm text-[#6E5A4E] leading-relaxed">
            All engagements begin with a written mandate. We never hold client capital and never charge
            success commissions tied to transactions.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((t, i) => (
            <article
              key={t.name}
              data-testid={`pricing-tier-${i}`}
              className={`relative flex flex-col p-8 md:p-10 border transition-all duration-500 ${
                t.accent
                  ? "bg-[#2B211A] text-[#EFEBE4] border-[#2B211A] md:-translate-y-3"
                  : "bg-[#F9F8F6] text-[#2B211A] border-[#D6CDBB]"
              }`}
            >
              {t.accent && (
                <span className="absolute -top-3 left-8 inline-flex items-center bg-[#8C735D] text-[#F9F8F6] text-[10px] uppercase tracking-[0.28em] px-3 py-1">
                  Most engaged
                </span>
              )}

              <div className={`text-[11px] uppercase tracking-[0.28em] ${t.accent ? "text-[#B89A7E]" : "text-[#8C735D]"}`}>
                {t.tagline}
              </div>
              <h3 className={`font-serif-display text-3xl md:text-[34px] mt-4 leading-tight ${t.accent ? "text-[#F9F8F6]" : "text-[#2B211A]"}`}>
                {t.name}
              </h3>

              <div className="mt-8 flex items-baseline gap-3">
                <span className={`font-serif-display text-5xl ${t.accent ? "text-[#F9F8F6]" : "text-[#2B211A]"}`}>
                  {t.price}
                </span>
              </div>
              <div className={`mt-1 text-sm ${t.accent ? "text-[#D6CDBB]" : "text-[#6E5A4E]"}`}>
                {t.cadence}
              </div>

              <ul className={`mt-8 space-y-3 text-[15px] ${t.accent ? "text-[#EFEBE4]" : "text-[#2B211A]"}`}>
                {t.features.map((f) => (
                  <li key={f} className="flex gap-3">
                    <span className={`mt-2 w-1.5 h-1.5 rounded-full shrink-0 ${t.accent ? "bg-[#B89A7E]" : "bg-[#8C735D]"}`} />
                    <span className={t.accent ? "text-[#EFEBE4]/95" : "text-[#2B211A]/90"}>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                data-testid={`pricing-cta-${i}`}
                className={`mt-10 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-medium font-body transition-colors duration-300 ${
                  t.accent
                    ? "bg-[#F9F8F6] text-[#2B211A] hover:bg-[#B89A7E] hover:text-[#F9F8F6]"
                    : "bg-[#2B211A] text-[#F9F8F6] hover:bg-[#8C735D]"
                }`}
              >
                {t.cta}
                <span aria-hidden>→</span>
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
